/**
 * Merchant (ATM/shop) onboarding tracker service.
 * Consumes the onboarding status API - the backoffice UI is just one consumer
 * of this API (an HMAC-secured variant is exposed to external partners too).
 */
import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { map } from "rxjs/operators";

import { environment } from "../../environments/environment";
import { AuthenticationService } from "./authentication.service";

export type OnboardingStageState = "PENDING" | "IN_PROGRESS" | "DONE" | "FAILED" | "REJECTED";

export interface OnboardingStageFailure {
    reason: string;
    failedAt: string;
}

export interface OnboardingStageRetry {
    retriedAt: string;
}

export interface OnboardingStage {
    key: string;
    name: string;
    order: number;
    state: OnboardingStageState;
    timestamp: string | null;
    failures: OnboardingStageFailure[];
    retries: OnboardingStageRetry[];
}

export interface Onboarding {
    onboardingId: string;
    merchant: {
        name: string;
        atmId: string | null;
        sonectId: string | null;
        referenceId: string | null;
        partnerId: string | null;
    };
    status: "IN_PROGRESS" | "COMPLETED" | "FAILED";
    currentStage: string;
    createdAt: string;
    updatedAt: string;
    stages: OnboardingStage[];
}

@Injectable()
export class OnboardingService {
    constructor(private http: HttpClient,
                private authenticationService: AuthenticationService) {
    }

    /* Fetch a merchant's onboarding journey by the ATM document id (with optional sonectId/referenceId fallbacks). */
    getStatusByAtmId(atmId: string, country: any, sonectId?: string, referenceId?: string) {
        const payload: any = {
            currentUser: this.authenticationService.getCurrentUser(),
            atmId,
            country
        };
        if (sonectId) {
            payload.sonectId = sonectId;
        }
        if (referenceId) {
            payload.referenceId = referenceId;
        }
        return this.http.post(
            environment.baseurl.api + "/protected/onboarding/status",
            payload, this.authenticationService.getHeaders()
        ).pipe(map(res => res));
    }

    /* Fetch a merchant's onboarding journey by partner + referenceId (SHOP_ID). */
    getStatusByReferenceId(referenceId: string, partnerId: string, country: any) {
        return this.http.post(
            environment.baseurl.api + "/protected/onboarding/status",
            {
                currentUser: this.authenticationService.getCurrentUser(),
                referenceId,
                partnerId,
                country
            }, this.authenticationService.getHeaders()
        ).pipe(map(res => res));
    }
}
